import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@egaranti/components";

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductService from "@/services/productService";

import { Loader2 } from "lucide-react";

const CategorySelector = ({
  onChange,
  value = {},
  showMainCategory = true,
  showCategory = true,
  showSubCategory = true,
  showBrand = true,
  col = 2, // Yeni prop: 1 veya 2 değeri alabilir
  showLabels = true, // Yeni prop ekledik
  showAllOption = true, // Yeni prop ekledik
}) => {
  const { t } = useTranslation();
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  // Add loading states
  const [isLoadingMain, setIsLoadingMain] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadMainCategories = async () => {
      setIsLoadingMain(true);
      try {
        const { data } = await ProductService.getMainCategories();
        if (cancelled) return;
        // EPS-4856 (B-14): BE response'da aynı kategori birden fazla kayıt
        // dönüyorsa (DB kirli — örn TCL × 2), id bazlı dedup uygula.
        const deduped = Array.isArray(data)
          ? Array.from(new Map(data.map((c) => [c.id, c])).values())
          : [];
        setMainCategories(deduped);
      } catch (error) {
        if (!cancelled) console.error("Failed to load main categories:", error);
      } finally {
        if (!cancelled) setIsLoadingMain(false);
      }
    };
    showMainCategory && loadMainCategories();
    return () => {
      cancelled = true;
    };
  }, [showMainCategory]);

  const getCategoriesAutoComplete = useCallback(async (mainCategoryId) => {
    setIsLoadingCategories(true);
    try {
      const { data } = await ProductService.getCategories(mainCategoryId);
      setCategories(data);
      setSubCategories([]);
      setBrands([]);
    } catch (error) {
      console.error("Failed to load categories:", error);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  const getSubCategoriesAutoComplete = useCallback(async (categoryId) => {
    setIsLoadingSubCategories(true);
    try {
      const { data } = await ProductService.getSubCategories(categoryId);
      setSubCategories(data);
      setBrands([]);
    } catch (error) {
      console.error("Failed to load subcategories:", error);
    } finally {
      setIsLoadingSubCategories(false);
    }
  }, []);

  const getBrandsAutoComplete = useCallback(async (subCategoryId) => {
    setIsLoadingBrands(true);
    try {
      const { data } = await ProductService.getSubCategoryBrands(subCategoryId);
      setBrands(data);
    } catch (error) {
      console.error("Failed to load brands:", error);
    } finally {
      setIsLoadingBrands(false);
    }
  }, []);

  const handleMainCategoryChange = useCallback(
    (mainCategoryId) => {
      // Eğer "Tümü" seçildiyse (boş string)
      if (!mainCategoryId) {
        onChange({
          mainCategoryId: "",
          categoryId: "",
          subCategoryId: "",
          subCategoryBrandId: "",
        });
        setCategories([]);
        setSubCategories([]);
        setBrands([]);
        return;
      }

      const newValue = {
        mainCategoryId,
        categoryId: "",
        subCategoryId: "",
        subCategoryBrandId: "",
      };
      onChange(newValue);
      getCategoriesAutoComplete(mainCategoryId);
    },
    [getCategoriesAutoComplete, onChange],
  );

  const handleCategoryChange = useCallback(
    (categoryId) => {
      // Eğer "Tümü" seçildiyse
      if (!categoryId) {
        onChange({
          ...value,
          categoryId: "",
          subCategoryId: "",
          subCategoryBrandId: "",
        });
        setSubCategories([]);
        setBrands([]);
        return;
      }

      const newValue = {
        ...value,
        categoryId,
        subCategoryId: "",
        subCategoryBrandId: "",
      };
      onChange(newValue);
      getSubCategoriesAutoComplete(categoryId);
    },
    [getSubCategoriesAutoComplete, onChange, value],
  );

  const handleSubCategoryChange = useCallback(
    (subCategoryId) => {
      // Eğer "Tümü" seçildiyse
      if (!subCategoryId) {
        onChange({
          ...value,
          subCategoryId: "",
          subCategoryBrandId: "",
        });
        setBrands([]);
        return;
      }

      const newValue = {
        ...value,
        subCategoryId,
        subCategoryBrandId: "",
      };
      onChange(newValue);
      getBrandsAutoComplete(subCategoryId);
    },
    [getBrandsAutoComplete, onChange, value],
  );

  const handleBrandChange = useCallback(
    (subCategoryBrandId) => {
      // Eğer "Tümü" seçildiyse
      if (!subCategoryBrandId) {
        onChange({
          ...value,
          subCategoryBrandId: "",
        });
        return;
      }

      const newValue = {
        ...value,
        subCategoryBrandId: subCategoryBrandId.toString(), // String'e çevirerek gönder
      };
      onChange(newValue);
    },
    [onChange, value],
  );

  // Load existing data if values are provided - Sıralamayı düzeltelim
  useEffect(() => {
    const loadData = async () => {
      if (value.mainCategoryId) {
        await getCategoriesAutoComplete(value.mainCategoryId);

        if (value.categoryId) {
          await getSubCategoriesAutoComplete(value.categoryId);

          if (value.subCategoryId) {
            await getBrandsAutoComplete(value.subCategoryId);
          }
        }
      }
    };

    loadData();
  }, [
    value.mainCategoryId,
    value.categoryId,
    value.subCategoryId,
    getCategoriesAutoComplete,
    getSubCategoriesAutoComplete,
    getBrandsAutoComplete,
  ]);

  // Create a loading indicator component
  const LoadingIndicator = () => <Loader2 className="h-4 w-4 animate-spin" />;

  return (
    <div
      className={`grid grid-cols-1 gap-4 ${col === 2 ? "md:grid-cols-2" : ""}`}
    >
      {showMainCategory && (
        <div className="flex flex-col gap-2">
          {showLabels && <Label>{t("productsPage.mainCategory")}</Label>}
          <Select
            value={value.mainCategoryId}
            onValueChange={handleMainCategoryChange}
            disabled={isLoadingMain}
            className={col === 1 ? "w-full" : ""}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("productsPage.mainCategory")} />
              {isLoadingMain && <LoadingIndicator />}
            </SelectTrigger>
            <SelectContent>
              {showAllOption && (
                <SelectItem value={null}>{t("common.all")}</SelectItem>
              )}
              {mainCategories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {t(category.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showCategory && (categories.length > 0 || isLoadingCategories) && (
        <div className="flex flex-col gap-2">
          {showLabels && <Label>{t("productsPage.category")}</Label>}
          <Select
            value={value.categoryId}
            onValueChange={handleCategoryChange}
            disabled={isLoadingCategories}
            className={col === 1 ? "w-full" : ""}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("productsPage.category")} />
              {isLoadingCategories && <LoadingIndicator />}
            </SelectTrigger>
            <SelectContent>
              {showAllOption && (
                <SelectItem value={null}>{t("common.all")}</SelectItem>
              )}
              {categories.map((category) => (
                <SelectItem key={category.id} value={category.id.toString()}>
                  {t(category.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showSubCategory &&
        (subCategories.length > 0 || isLoadingSubCategories) && (
          <div className="flex flex-col gap-2">
            {showLabels && <Label>{t("productsPage.subCategory")}</Label>}
            <Select
              value={value.subCategoryId}
              onValueChange={handleSubCategoryChange}
              disabled={isLoadingSubCategories}
              className={col === 1 ? "w-full" : ""}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("productsPage.subCategory")} />
                {isLoadingSubCategories && <LoadingIndicator />}
              </SelectTrigger>
              <SelectContent>
                {showAllOption && (
                  <SelectItem value={null}>{t("common.all")}</SelectItem>
                )}
                {subCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {t(category.name)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

      {showBrand && (brands.length > 0 || isLoadingBrands) && (
        <div className="flex flex-col gap-2">
          {showLabels && <Label>{t("productsPage.brand")}</Label>}
          <Select
            value={value.subCategoryBrandId?.toString()}
            onValueChange={handleBrandChange}
            disabled={isLoadingBrands}
            className={col === 1 ? "w-full" : ""}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("productsPage.brand")} />
              {isLoadingBrands && <LoadingIndicator />}
            </SelectTrigger>
            <SelectContent>
              {showAllOption && (
                <SelectItem value={null}>{t("common.all")}</SelectItem>
              )}
              {brands.map((brand) => (
                <SelectItem
                  key={brand.id}
                  value={brand.id.toString()} // toString() ekleyelim
                >
                  {brand.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
